
/* ============================== src/components/ColoredSpan.jsx ============================== */
// src/components/ColoredSpan.jsx
import { useLayoutEffect, useRef } from "react";

/**
 * Span, у которого цвет приходит из справочника (TASK_STATUSES,
 * PRIORITIES, ROLES, PROJECT_PRIORITIES) и передаётся в CSS через
 * переменную --chip-color. Цвет ставится императивно в useLayoutEffect,
 * тот же паттерн, что у PriorityStrip, TaskProgress, WorkloadBar,
 * ProjectProgress.
 *
 * Класс оформления задаёт вызывающий: .st-chip.st-chip--tinted для
 * фонового тинта, .rep-priority для цветного текста без фона,
 * .prio-chip - для чипа приоритета в карточках, .role-chip--tinted -
 * для чипов ролей в «Персонале». Компонент про эти классы ничего
 * не знает - это ответственность точки вызова, каждый класс живёт
 * в своём CSS-разделе.
 *
 * Пустое или null-значение color не ломает рендер: setProperty('')
 * снимает переменную, CSS подставит fallback из var(--chip-color,
 * ...) в зависимости от класса.
 *
 * ...rest прокидывается на <span>. Это нужно для семантических
 * атрибутов, которые не являются ни цветом, ни классом: title
 * (подсказка), data-* (например, для тестов или микроразметки),
 * aria-* - всё это естественно принадлежит span'у, и оборачивать
 * его в ещё один span только ради них не нужно.
 *
 * ...rest идёт последним: если вызывающий по какой-то причине
 * передаст className или ref в rest, они перезапишут вычисленные
 * значения. Это осознанное решение - вызывающий знает, что делает,
 * и передача одноимённого атрибута в rest означает «я хочу
 * переопределить».
 */
export function ColoredSpan({ color, className = "", children, ...rest }) {
  const ref = useRef(null);

  useLayoutEffect(() => {
    ref.current?.style.setProperty("--chip-color", color || "");
  }, [color]);

  return (
    <span ref={ref} className={className} {...rest}>
      {children}
    </span>
  );
}

/**
 * Цветная точка (8–9 px). Отличие от ColoredSpan - ставит не
 * --chip-color, а --dot-color, которую CSS использует для background.
 * Отдельный компонент, а не параметризация ColoredSpan по имени
 * переменной: имена семантические (chip и dot - разные роли), и
 * держать их как две тонкие функции понятнее, чем один универсал
 * с varName.
 *
 * Используется в карточках задач/проектов (класс .pdot), в заголовке
 * колонки канбана (.kdot), в календаре (.pdot) и в Ганте (проект-точка
 * - тоже .pdot, цвет через --dot-color). Ничего про конкретные классы
 * не знает - className приходит от вызывающего.
 *
 * aria-hidden="true" по умолчанию: точка - визуальный маркер,
 * не несёт смысловой нагрузки для скринридера. Если когда-нибудь
 * потребуется семантика, вызывающий передаст title - тогда
 * aria-hidden снимется автоматически.
 */
export function ColorDot({ color, className, title }) {
  const ref = useRef(null);
  useLayoutEffect(() => {
    ref.current?.style.setProperty("--dot-color", color || "");
  }, [color]);
  return (
    <span
      ref={ref}
      className={className}
      aria-hidden={title ? undefined : "true"}
      title={title || undefined}
    />
  );
}
